import React, { useState, useEffect } from 'react';
import {
  Autocomplete,
  Box,
  Button,
  Grid,
  TextField,
  Typography,
  FormControl,
  MenuItem,
  InputLabel,
  Select,
  Container,
} from '@mui/material';
import DeleteIcon from '@mui/icons-material/Delete';
import { useRecoilState, useRecoilValue, useSetRecoilState } from 'recoil';
import {
  artistState,
  loadingState,
  paginationModelState,
  songOptionsState,
  songlistState,
  toastState,
} from '../state/RecoilState';
import RestUtilities from '../services/RestUtilities';
import { Song, SongListitem } from '../@types/api';
import { DataGrid, GridActionsCellItem, GridRowParams } from '@mui/x-data-grid';
import MustPickArtist from '../components/Common/MustPickArtist';

export interface ArtistOption {
  id: string;
  label: string;
}

interface NewSong {
  songName: string;
  songArtistName: string;
  catId: number;
}

const defaultSong: NewSong = {
  songName: '',
  songArtistName: '',
  catId: 8,
};

const Songs = () => {
  const setLoading = useSetRecoilState<boolean>(loadingState);
  const setToast = useSetRecoilState(toastState);
  const [songlist, setSonglist] = useRecoilState<SongListitem[] | undefined>(
    songlistState
  );
  const artist = useRecoilValue(artistState);
  const [newSong, setNewSong] = useState<NewSong>(defaultSong);
  const [songOptions, setSongOptions] = useRecoilState(songOptionsState);
  const [paginationModel, setPaginationModel] =
    useRecoilState(paginationModelState);
  const [song, setSong] = useState<Song | null>(null);

  const handleAddNewClick = async () => {
    const songName = newSong.songName.replace(/\b\w/g, (l) => l.toUpperCase());
    const songArtistName = newSong.songArtistName.replace(/\b\w/g, (l) =>
      l.toUpperCase()
    );
    const url = `api/Repertoire/NewSong/${artist?.artistId}`;
    const data = {
      songName,
      songArtistName,
      catId: newSong.catId,
    };
    const response = await RestUtilities.post(url, data);
    if (response.ok) {
      const newSonglist = [response.content, ...(songlist as SongListitem[])];
      setSonglist(newSonglist);
      setToast('New song added');
    }
  };

  const handleAddClick = async (song: Song | null) => {
    if (!song) return;
    const isAlreadyPresent =
      songlist && !!songlist.find((x: any) => x.songId === song.id);
    if (isAlreadyPresent) return;
    const url = `api/Repertoire/${artist?.artistId}`;
    const artistRepertoire = {
      songId: song.id,
      artistId: artist?.artistId,
    };
    const response = await RestUtilities.post(url, artistRepertoire);
    if (response.ok) {
      const newSonglist = [response.content, ...(songlist as SongListitem[])];
      setSonglist(newSonglist);
      setToast('Song added');
    }
  };

  const handleDeleteSong = async (params: GridRowParams) => {
    setLoading(true);
    const repId = params.row.repId;
    const url = `api/Repertoire/${repId}/${artist?.artistId}`;
    const response = await RestUtilities.delete(url);
    if (response.ok) {
      const updatedSongList = songlist?.filter((x: any) => x.repId !== repId);
      setSonglist(updatedSongList);
      setToast('Song deleted');
    }
    setLoading(false);
  };

  useEffect(() => {
    const fetchRepertoire = async () => {
      setLoading(true);
      const url = `api/Repertoire/${artist?.artistId}`;
      const response = await RestUtilities.get(url);
      if (response.ok) {
        setSonglist(response.content);
        setToast('Songs loaded');
      }
      setLoading(false);
    };

    if (artist && !songlist) {
      fetchRepertoire();
    }
  }, [artist, setLoading, setSonglist, setToast, songlist]);

  useEffect(() => {
    const refreshData = async () => {
      setLoading(true);
      const response = await RestUtilities.get('api/Repertoire/');
      if (response.ok) {
        const songs: Song[] = response.content;
        const songOptions: Song[] = [];
        songs.forEach((x) => {
          const artistName = x.songArtistName ? x.songArtistName.trim() : '';
          const found = songOptions.find(
            (s) => s.label === x.songName && s.artist === artistName
          );
          if (!found) {
            songOptions.push({
              id: x.songId,
              label: x.songName,
              artist: artistName,
              catId: x.catId,
              catDescription: x.category.catDescription,
            } as Song);
          }
        });
        setSongOptions(songOptions);
        setLoading(false);
      }
    };
    if (!songOptions) {
      refreshData();
    }
  }, [setLoading, setSongOptions, songOptions]);

  if (!artist) {
    return <MustPickArtist />;
  }
  return (
    <Container maxWidth='lg'>
      <Typography variant='h4' gutterBottom>
        {artist.artistName} Songlist
      </Typography>
      <Grid container spacing={2}>
        <Grid item xs={12}>
          <Typography variant='body2'>
            We have a database of songs already where you can select a song
            quickly an easily. If you cannot find your song/artist entry in the
            existing database then you can create a new entry. Always search for
            the song first and create a new song as a last resort. This way we
            will avoid duplication of effort. Take care when creating a new song
            entry as spelling mistakes cannot be changed easily!
          </Typography>
        </Grid>
        <Grid item sx={{ maxWidth: '580px' }} mt={1}>
          {songlist && (
            <DataGrid
              rows={songlist}
              columns={[
                { field: 'repId', headerName: 'RepId', width: 60 },
                {
                  field: 'songName',
                  headerName: 'SongName',
                  width: 200,
                  valueGetter: (params) => params.row.song.songName,
                },
                {
                  field: 'artistName',
                  headerName: 'ArtistName',
                  width: 150,
                  valueGetter: (params) => params.row.song.songArtistName,
                },
                {
                  field: 'categoryDescription',
                  headerName: 'Decade',
                  width: 50,
                  valueGetter: (params) =>
                    params.row.song.category.catDescription,
                },
                {
                  field: 'actions',
                  type: 'actions',
                  getActions: (params: GridRowParams) => [
                    <GridActionsCellItem
                      icon={<DeleteIcon />}
                      onClick={() => handleDeleteSong(params)}
                      label='Delete'
                    />,
                  ],
                },
              ]}
              density='compact'
              getRowId={(row) => row.repId}
              pageSizeOptions={[25, 50, 100, 250]}
              paginationModel={paginationModel}
              onPaginationModelChange={setPaginationModel}
            />
          )}
        </Grid>
        <Grid item mt={1} xs>
          <Typography variant='body2' mb={2}>
            Find an existing song in our system and add to your band's setlist
          </Typography>
          {songOptions && (
            <Box
              display='flex'
              alignItems={'center'}
              justifyContent={'flex-start'}
            >
              <Autocomplete
                disablePortal
                sx={{ width: 300, mr: '5px' }}
                autoComplete={true}
                autoSelect={true}
                value={song}
                onChange={(event: any, newValue: Song | null) => {
                  setSong(newValue);
                }}
                id='combo-box-demo'
                options={songOptions}
                getOptionLabel={(option) => option.label}
                renderInput={(params) => (
                  <TextField {...params} placeholder='Start typing song name' />
                )}
                renderOption={(props, option) => (
                  <Box component='li' {...props} key={option.id}>
                    {option.label} ({option.artist}) {option.catDescription}
                  </Box>
                )}
                size='small'
              />
              <Button
                variant='outlined'
                onClick={() => handleAddClick(song)}
                sx={{ px: 2 }}
                size='small'
              >
                Add Song
              </Button>
            </Box>
          )}
          <Box my={2}>
            <Typography variant='body2' mt={3}>
              Or add a new song to our system
            </Typography>
            <FormControl sx={{ minWidth: '100%', my: 2 }}>
              <TextField
                required
                fullWidth
                label='Song name'
                value={newSong.songName}
                placeholder='Song name'
                size='small'
                onChange={(e) => {
                  setNewSong({ ...newSong, songName: e.target.value });
                }}
              />
            </FormControl>
            <FormControl sx={{ minWidth: '100%', my: 2 }}>
              <TextField
                required
                fullWidth
                label='Artist name'
                value={newSong.songArtistName}
                placeholder='Artist name'
                size='small'
                onChange={(e) => {
                  setNewSong({ ...newSong, songArtistName: e.target.value });
                }}
              />
            </FormControl>
            <FormControl sx={{ minWidth: '100%', my: 2 }}>
              <InputLabel id='category-label'>Category / Decade</InputLabel>
              <Select
                fullWidth
                labelId='category-label'
                id='category-selector'
                label='Category / Decade'
                value={newSong.catId}
                onChange={(e) =>
                  setNewSong({ ...newSong, catId: Number(e.target.value) })
                }
                variant='outlined'
                size='small'
              >
                {categoryConfig.map((c) => (
                  <MenuItem value={c.id} key={c.id}>
                    {c.description}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
            <Button
              variant='outlined'
              onClick={handleAddNewClick}
              sx={{ px: 2 }}
              size='small'
            >
              Add new Song
            </Button>
          </Box>
        </Grid>
      </Grid>
    </Container>
  );
};

export default Songs;

const categoryConfig = [
  { id: 8, description: '20s' },
  { id: 7, description: '10s' },
  { id: 13, description: '00s' },
  { id: 5, description: '90s' },
  { id: 4, description: '80s' },
  { id: 3, description: '70s' },
  { id: 2, description: '60s' },
  { id: 1, description: '50s' },
  { id: 9, description: 'Folk/Traditional' },
  { id: 10, description: 'Various' },
  { id: 11, description: 'Classical' },
  { id: 12, description: 'Ceremony and Reception Music' },
  { id: 14, description: '2 Tone/Trojan/Rocksteady/Reggae' },
];
